import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiGet, apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3, H5, ProgressBar } from "../../AbstractElements";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2 } from 'react-feather';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";

// Define Interface for Driver
interface DriverData {
    id: number;
    name: string;
    email: string; // Added
    phone_number: string;
    status: string;
    gender: string; // Added
    date_of_birth: string; // Added
    address: string; // Added
    blood_group: string; // Added
    nationality: string; // Added
    license_number: string;
    license_expiry_date: string; // Added
    joining_date: string; // Added
    years_of_experience: number;
    assigned_branch_id: string | number; // Updated type
    license_image_front: string; // Added
    license_image_back: string; // Added
    branch?: { branch_name: string };
}

export default function ViewDriver() {
    const [data, setData] = useState<DriverData[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");
    const prevSearch = React.useRef(searchTerm);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        const delay = isSearchChanged ? 500 : 0;
        prevSearch.current = searchTerm;

        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };

            if (sortCol) {
                // console.log("Sending sort params:", sortCol, sortDir);
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("drivers/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total || 0);
            } else {
                setData(response);
                setTotalRows(response.length || 0);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        setLoading(true);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setLoading(true);
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_driver`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_driver`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("drivers/delete", { id: id });
                    toast.success("Driver deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete driver", { autoClose: 1000 });
                }
            }
        });
    };

    const calculateProfileCompletion = (row: DriverData) => {
        const mandatoryFields = [
            'name', 'email', 'phone_number', 'status', 'gender', 'date_of_birth',
            'address', 'blood_group', 'nationality', 'license_number',
            'license_expiry_date', 'joining_date', 'years_of_experience',
            'assigned_branch_id', 'license_image_front', 'license_image_back'
        ];
        let filledCount = 0;
        mandatoryFields.forEach(field => {
            if (row[field as keyof DriverData]) {
                filledCount++;
            }
        });
        return Math.min(Math.round((filledCount / mandatoryFields.length) * 100), 100);
    };

    const columns = [
        {
            name: "Sl. No",
            selector: (row: DriverData, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "80px",
        },
        {
            name: "Name",
            selector: (row: DriverData) => row.name,
            sortable: true,
            sortField: "name",
            wrap: true,
        },
        {
            name: "Phone",
            selector: (row: DriverData) => row.phone_number,
            sortable: true,
            sortField: "phone_number",
            wrap: true,
        },
        {
            name: "License No",
            selector: (row: DriverData) => row.license_number,
            sortable: true,
            sortField: "license_number",
            wrap: true,
        },
        {
            name: "Branch",
            selector: (row: DriverData) => row.branch?.branch_name || row.assigned_branch_id || "N/A",
            sortable: true,
            sortField: "branch_name",
            wrap: true,
        },
        {
            name: "Profile Status",
            cell: (row: DriverData) => {
                const completion = calculateProfileCompletion(row);
                return (
                    <div className="w-100">
                        <div className="progress-bar-animated progress-bar-striped">
                            <ProgressBar
                                value={completion}
                                color={completion === 100 ? 'success' : 'warning'}
                                striped={completion <= 100}
                                animated={completion < 100}
                                style={{ height: '8px' }}
                            />
                        </div>
                        <div className="text-center small mt-1">{completion}%</div>
                    </div>
                );
            },
            sortable: false,
            width: "150px"
        },
        {
            name: "Status",
            cell: (row: DriverData) => (
                <span className={`badge ${row.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                    {row.status}
                </span>
            ),
            sortable: true,
            sortField: "status",
        },
        {
            name: "Actions",
            cell: (row: DriverData) => (
                <div className="d-flex justify-content-between gap-2">

                    <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                        <Edit size={14} />
                    </Button>
                    {hasPermission('driver.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1', // Light teal background
                color: '#00695c', // Dark teal text
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px', // override the row height
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs
                pageTitle="View Driver"
                parent="General"
                title="View Driver"
            />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Driver Overview</H3>
                                {hasPermission('driver.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Driver
                                    </Btn>
                                )}
                            </Col>
                            {hasPermission('driver.view') && (<>
                                <Col xs={12} className="mb-3 d-flex justify-content-end">
                                    <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                </Col>
                                <div className="table-responsive">
                                    <DataTable
                                        data={data}
                                        columns={columns}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        onChangePage={handlePageChange}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onSort={handleSort}
                                        sortServer
                                        progressPending={loading}
                                        progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                        highlightOnHover
                                        customStyles={customStyles}
                                    />
                                </div>
                            </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Container>
            <ToastContainer autoClose={1000} />
        </div>
    );
}
